/**
 * LandingMotivation — "Motivation & Innovations"
 * Two dashboard-style cards on ink-50: daily quote + bank details (with copy).
 */
import { Copy, Check, Landmark, Lightbulb } from 'lucide-react';
import { useState } from 'react';
import LandingDailyQuote from './LandingDailyQuote';

const BANK_DETAILS = [
    ['A/C Name', 'Think eBikeGo Pvt. Ltd.'],
    ['Brand', 'Ageon'],
    ['Bank', 'ICICI Bank'],
    ['A/C No', '123805009764'],
    ['Branch', 'Andheri, Mumbai'],
    ['IFSC', 'ICIC0001238'],
];

const BANK_DETAILS_TEXT = BANK_DETAILS.map(([k, v]) => `${k}: ${v}`).join('\n');

async function copyToClipboard(text) {
    if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
        return;
    }
    const ta = document.createElement('textarea');
    ta.value = text;
    ta.setAttribute('readonly', '');
    ta.style.position = 'fixed';
    ta.style.left = '-9999px';
    document.body.appendChild(ta);
    ta.select();
    document.execCommand('copy');
    document.body.removeChild(ta);
}

export default function LandingMotivation() {
    const [copied, setCopied] = useState(false);

    const copyBankDetails = async () => {
        try {
            await copyToClipboard(BANK_DETAILS_TEXT);
            setCopied(true);
            setTimeout(() => setCopied(false), 1500);
        } catch {
            setCopied(false);
        }
    };

    return (
        <section className="relative bg-ink-50">
            <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12 py-20 lg:py-28">
                {/* Header */}
                <div className="text-center max-w-2xl mx-auto mb-12 lg:mb-14" data-aos="fade-up">
                    <span className="lp-chip mb-6">
                        Daily insights
                    </span>
                    <h2 className="font-heading font-extrabold tracking-tight text-3xl sm:text-4xl lg:text-5xl text-ink-900">
                        Motivation &amp;{' '}
                        <span className="bg-gradient-to-r from-brand-teal to-brand-teal-700 bg-clip-text text-transparent">
                            Innovations.
                        </span>
                    </h2>
                    <p className="mt-5 text-ink-600 leading-relaxed text-lg">
                        Stay motivated and innovative with our daily insights.
                    </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8">
                    {/* Card 1 — daily quote */}
                    <div data-aos="fade-up" data-aos-delay="80">
                        <LandingDailyQuote />
                    </div>

                    {/* Card 2 — bank details */}
                    <div
                        className="lp-lift relative h-full flex flex-col overflow-hidden rounded-3xl bg-white p-7 lg:p-8 shadow-card ring-1 ring-ink-100"
                        data-aos="fade-up"
                        data-aos-delay="160"
                    >
                        <div aria-hidden className="pointer-events-none absolute -top-10 -right-10 h-32 w-32 rounded-full bg-brand-gold/15 blur-2xl" />

                        <div className="flex items-center justify-between gap-4 mb-6">
                            <div className="flex items-center gap-3">
                                <span className="grid h-10 w-10 place-items-center rounded-xl bg-gradient-to-br from-brand-teal to-brand-teal-700 text-white shadow-lg shadow-brand-teal/20">
                                    <Landmark className="h-[1.1rem] w-[1.1rem]" />
                                </span>
                                <span className="text-[11px] uppercase tracking-[0.22em] font-bold text-ink-500">
                                    Bank Details
                                </span>
                            </div>
                            <button
                                type="button"
                                onClick={copyBankDetails}
                                className={`inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-xs font-bold transition ${
                                    copied
                                        ? 'bg-brand-teal text-white'
                                        : 'border border-ink-300 text-ink-700 hover:border-brand-teal hover:text-brand-teal-700'
                                }`}
                            >
                                {copied ? <Check size={13} /> : <Copy size={13} />}
                                {copied ? 'Copied!' : 'Copy'}
                            </button>
                        </div>

                        <dl className="flex-1 divide-y divide-ink-100">
                            {BANK_DETAILS.map(([label, value]) => (
                                <div key={label} className="flex items-baseline justify-between gap-4 py-3">
                                    <dt className="text-xs font-bold uppercase tracking-wide text-ink-500 shrink-0">
                                        {label}
                                    </dt>
                                    <dd className="font-mono text-sm font-medium text-ink-900 text-right">{value}</dd>
                                </div>
                            ))}
                        </dl>
                    </div>
                </div>
            </div>
        </section>
    );
}
